"use client";

import { SelectItem } from "@cmp-src/form/UnifiedSelect";
import type { VisitCreatePayload } from "@gettactic/api/src/endpoints/visitor-manager";
import {
  VisitorType,
  VisitorTypeField
} from "@gettactic/api/src/endpoints/visitor-manager";
import { HookFormCheckboxInput } from "@gettactic/components/src/form/CheckboxInput";
import { HookFormEmailInput } from "@gettactic/components/src/form/EmailInput";
import { HookFormCalendarInput } from "@gettactic/components/src/form/FormCalendarInput";
import { HookFormOfficeSelectorInput } from "@gettactic/components/src/form/OfficeSelectorInput";
import {
  HookFormPhoneInput,
  resolveDefaultCountry
} from "@gettactic/components/src/form/PhoneInput";
import { HookFormTextareaInput } from "@gettactic/components/src/form/TextareaInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormTimeSelect } from "@gettactic/components/src/form/TimeSelect";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { Stack } from "@gettactic/components/src/primitives";
import { HStack } from "@gettactic/components/src/primitives/Stack/HStack";
import { MIN_HOST_SEARCH_LENGTH } from "@gettactic/hooks/src/hooks/kiosk";
import { useQuery } from "@tanstack/react-query";
import { format, parse, setHours, setMinutes } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { api } from "@/lib/api/api";

export const preRegisterSchema = z
  .object({
    office_id: z.string().min(1, "Office is required"),
    visitor_type_id: z.string().optional(),
    host_id: z.string().optional(),
    visitor_name: z.string().min(1, "Visitor name is required"),
    visitor_email: z.string().trim().email("A valid email is required"),
    expected_date: z.date({ message: "Expected visit date is required" }),
    expected_time: z.string().min(1, "Expected visit time is required")
  })
  .passthrough();

export type PreRegisterFormValues = z.infer<typeof preRegisterSchema>;

export const PRE_REGISTER_DEFAULT_VALUES: PreRegisterFormValues = {
  office_id: "",
  visitor_type_id: "",
  host_id: "",
  visitor_name: "",
  visitor_email: "",
  expected_date: undefined as unknown as Date,
  expected_time: ""
};

/**
 * Builds the API payload for creating a visitor pre-registration
 * from form values and the current set of custom fields.
 */
export function buildVisitCreatePayload(
  data: PreRegisterFormValues,
  customFields: VisitorTypeField[]
): VisitCreatePayload {
  const parsedTime = parse(data.expected_time, "hh:mm a", new Date());
  const expectedDateTime = setMinutes(
    setHours(data.expected_date, parsedTime.getHours()),
    parsedTime.getMinutes()
  );

  const visitFields = customFields
    .map((field) => ({
      field_id: field.id,
      // Checkboxes always answer: an unticked one is recorded as "false" rather
      // than dropped, so the refusal is visible on the visit.
      value:
        field.field_type === "checkbox"
          ? String(data[field.id] === "true")
          : String(data[field.id] ?? "")
    }))
    .filter((f) => f.value);

  return {
    office_id: data.office_id,
    visitor_type_id: data.visitor_type_id || undefined,
    host_id: data.host_id || undefined,
    visitor_name: data.visitor_name,
    visitor_email: data.visitor_email,
    expected: expectedDateTime.toISOString(),
    visit_fields: visitFields.length > 0 ? visitFields : undefined
  };
}

interface VisitorPreRegistrationFormFieldsProps {
  form: UseFormReturn<PreRegisterFormValues>;
  isActive: boolean;
  readOnlyEmail?: boolean;
  customFieldsRef?: React.MutableRefObject<VisitorTypeField[]>;
}

export const VisitorPreRegistrationFormFields: React.FC<
  VisitorPreRegistrationFormFieldsProps
> = ({ form, isActive, readOnlyEmail, customFieldsRef }) => {
  const [hostSearch, setHostSearch] = useState("");
  const [selectedHostItem, setSelectedHostItem] = useState<SelectItem | null>(
    null
  );
  const isInitialMount = useRef(true);

  const selectedOfficeId = form.watch("office_id");
  const selectedVisitorTypeId = form.watch("visitor_type_id");
  const selectedHostId = form.watch("host_id");

  // Only offices with an office-type visitor station take visitors in; room
  // and wayfinder stations don't
  const { data: stationOffices } = useQuery({
    queryKey: ["visitorStations.listOffices"],
    queryFn: async () => {
      const response = await api.client.visitorStations.listOffices();
      return response.result;
    },
    enabled: isActive
  });

  // Empty while loading, so no office is listed only to disappear after
  const stationOfficeIds = useMemo(
    () =>
      stationOffices
        ?.filter((stationOffice) =>
          stationOffice.stations.some((station) => station.type === "office")
        )
        .map((stationOffice) => stationOffice.office.id) ?? [],
    [stationOffices]
  );

  // Phone numbers default to the selected office's country, like the kiosk
  const defaultPhoneCountry = useMemo(
    () =>
      resolveDefaultCountry(
        stationOffices?.find(
          (stationOffice) => stationOffice.office.id === selectedOfficeId
        )?.office.country
      ),
    [stationOffices, selectedOfficeId]
  );

  // Fetch the visitor types allowed at the selected office
  const { data: visitorTypesData } = useQuery({
    queryKey: ["visitorTypes.list", { office_id: selectedOfficeId || null }],
    queryFn: async () => {
      const response =
        await api.client.visitorStations.listOfficeTypes(selectedOfficeId);
      return response.result;
    },
    enabled: isActive && !!selectedOfficeId
  });

  const visitorTypeItems: SelectItem[] = useMemo(() => {
    if (!visitorTypesData) return [];
    return visitorTypesData.map((vt) => ({
      id: vt.id,
      name: vt.name
    }));
  }, [visitorTypesData]);

  const selectedVisitorType: VisitorType | undefined = useMemo(() => {
    if (!selectedVisitorTypeId || !visitorTypesData) return undefined;
    return visitorTypesData.find((vt) => vt.id === selectedVisitorTypeId);
  }, [selectedVisitorTypeId, visitorTypesData]);

  const requiresHost = selectedVisitorType?.requires_host === true;

  // A visitor type with specific hosts lists them right away; when anyone can
  // be a host, a few characters are needed so the directory isn't exposed
  const trimmedHostSearch = hostSearch.trim();
  const isHostSearchTooShort =
    selectedVisitorType?.all_users_hosts !== false &&
    trimmedHostSearch.length < MIN_HOST_SEARCH_LENGTH;

  const customFields = useMemo(() => {
    if (!selectedVisitorType) return [];
    let nameSkipped = false;
    let emailSkipped = false;
    return selectedVisitorType.fields.filter((f) => {
      if (f.field_type === "name" && !nameSkipped) {
        nameSkipped = true;
        return false;
      }
      if (f.field_type === "email" && !emailSkipped) {
        emailSkipped = true;
        return false;
      }
      return true;
    });
  }, [selectedVisitorType]);

  // Expose custom fields to parent for submission payload building
  useEffect(() => {
    if (customFieldsRef) {
      customFieldsRef.current = customFields;
    }
  }, [customFields, customFieldsRef]);

  // Fetch hosts for the selected visitor type, only once enough is typed
  const { data: hostsData } = useQuery({
    queryKey: [
      "visitorTypes.listHosts",
      selectedVisitorTypeId,
      trimmedHostSearch
    ],
    queryFn: async () => {
      const response = await api.client.visitorTypes.listHosts(
        selectedVisitorTypeId!,
        { search: trimmedHostSearch || undefined, limit: 20 }
      );
      return response.result;
    },
    enabled:
      isActive &&
      !!selectedVisitorTypeId &&
      requiresHost &&
      !isHostSearchTooShort
  });

  const searchedHostItems: SelectItem[] = useMemo(() => {
    if (isHostSearchTooShort || !hostsData?.elements) return [];
    return hostsData.elements.map((host) => ({
      id: host.id,
      name: host.name
    }));
  }, [hostsData, isHostSearchTooShort]);

  // The select resolves its label from the listed items, so the chosen host
  // is remembered to keep showing once the search is cleared
  useEffect(() => {
    const searchedHost = searchedHostItems.find(
      (host) => host.id === selectedHostId
    );
    if (searchedHost) {
      setSelectedHostItem(searchedHost);
    }
  }, [searchedHostItems, selectedHostId]);

  const hostItems: SelectItem[] = useMemo(() => {
    const isSelectedHostListed = searchedHostItems.some(
      (host) => host.id === selectedHostId
    );
    if (
      !selectedHostItem ||
      selectedHostItem.id !== selectedHostId ||
      isSelectedHostListed
    ) {
      return searchedHostItems;
    }
    return [selectedHostItem, ...searchedHostItems];
  }, [searchedHostItems, selectedHostItem, selectedHostId]);

  // Reset visitor_type_id and host_id when office changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    if (selectedOfficeId) {
      form.setValue("visitor_type_id", "");
      form.setValue("host_id", "");
    }
  }, [selectedOfficeId, form]);

  // Reset host_id when visitor type changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    form.setValue("host_id", "");
    setHostSearch("");
  }, [selectedVisitorTypeId, form]);

  // biome-ignore lint/suspicious/noExplicitAny: dynamic field names not in static schema
  const formForInputs = form as any;

  return (
    <Stack>
      <HStack>
        <HookFormOfficeSelectorInput
          form={form}
          name="office_id"
          label="Office"
          isRequired
          selectableOfficeIds={stationOfficeIds}
        />

        <HookFormUnifiedSelectInput
          form={form}
          name="visitor_type_id"
          label="Visitor Type"
          items={visitorTypeItems}
          placeholder={
            !selectedOfficeId
              ? "Select an office first"
              : "Select a visitor type"
          }
          disabled={!selectedOfficeId}
        />
      </HStack>

      {requiresHost && (
        <HookFormUnifiedSelectInput
          form={form}
          name="host_id"
          label="Host"
          items={hostItems}
          searchable
          onSearchChange={setHostSearch}
          placeholder="Search for a host"
          emptyMessage={
            isHostSearchTooShort
              ? `Type at least ${MIN_HOST_SEARCH_LENGTH} characters to search for a host`
              : "No hosts found"
          }
        />
      )}

      <HStack>
        <HookFormTextInput
          form={form}
          name="visitor_name"
          label="Visitor Name"
          placeholder="Full name"
          isRequired
        />

        <HookFormTextInput
          form={form}
          name="visitor_email"
          label="Visitor Email"
          placeholder="Email address"
          isRequired
          disabled={readOnlyEmail}
        />
      </HStack>

      {customFields.map((field) => (
        <CustomFieldInput
          key={field.id}
          field={field}
          form={formForInputs}
          defaultPhoneCountry={defaultPhoneCountry}
        />
      ))}

      <HStack>
        <HookFormCalendarInput
          form={form}
          name="expected_date"
          label="Expected Visit Date"
          placeholder="Select Date"
          mode="single"
          variant="outlined"
          setValueOnApply={false}
          isRequired
        />
        <HookFormTimeSelect
          form={form}
          name="expected_time"
          label="Expected Visit Time"
          placeholder="Select Time"
          isRequired
        />
      </HStack>
    </Stack>
  );
};

interface CustomFieldInputProps {
  field: VisitorTypeField;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic form type for custom fields
  form: any;
  defaultPhoneCountry: ReturnType<typeof resolveDefaultCountry>;
}

const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
  field,
  form,
  defaultPhoneCountry
}) => {
  if (field.field_type === "phone") {
    return (
      <HookFormPhoneInput
        form={form}
        name={field.id}
        label={field.name}
        defaultCountry={defaultPhoneCountry}
      />
    );
  }

  // Acknowledgements are signed by the visitor at check-in, so the host
  // pre-registering them is never blocked by a required checkbox — the API
  // only enforces required fields on the check-in itself.
  if (field.field_type === "checkbox") {
    return (
      <HookFormCheckboxInput
        form={form}
        name={field.id}
        valueMode="string"
        checkboxPosition="left"
        label={field.name}
        description={field.description}
        isRequired={field.is_required}
        rules={{ required: false }}
      />
    );
  }

  if (field.field_type === "textarea") {
    return (
      <HookFormTextareaInput
        form={form}
        name={field.id}
        label={field.name}
        placeholder={field.description || `Enter ${field.name.toLowerCase()}`}
      />
    );
  }

  if (field.field_type === "email") {
    return (
      <HookFormEmailInput
        form={form}
        name={field.id}
        label={field.name}
        placeholder={field.description || "email@example.com"}
      />
    );
  }

  if (field.field_type === "name") {
    return (
      <HookFormTextInput
        form={form}
        name={field.id}
        label={field.name}
        placeholder={field.description || field.name}
      />
    );
  }

  // Default: text field
  return (
    <HookFormTextInput
      form={form}
      name={field.id}
      label={field.name}
      placeholder={field.description || `Enter ${field.name.toLowerCase()}`}
    />
  );
};
